import React from "react";
import RALDRealtyCard from "@/components/shared/partner/RALDRealtyCard";
import { PartnerProperty } from "@/types/partner";
import { RALD_REALTY_CONFIG } from "@/utils/partnerBranding";

interface RALDRealtySectionProps {
  properties: PartnerProperty[];
  onPropertyClick?: (property: PartnerProperty) => void;
  loading?: boolean;
}

const RALDRealtySection: React.FC<RALDRealtySectionProps> = ({
  properties,
  onPropertyClick,
  loading = false,
}) => {
  const colors = {
    primary: RALD_REALTY_CONFIG.primaryColor,
    secondary: RALD_REALTY_CONFIG.secondaryColor,
    accent: RALD_REALTY_CONFIG.accentColor,
  };

  if (loading) {
    return (
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-[541px] bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (properties.length === 0) {
    return null;
  }

  return (
    <section
      className="py-12 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: "#f8faf8" }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <img
              src={RALD_REALTY_CONFIG.logoUrl}
              alt={RALD_REALTY_CONFIG.name}
              className="h-12 w-auto object-contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
              }}
            />
            <div>
              <h2
                className="text-3xl sm:text-4xl font-bold"
                style={{ color: colors.primary }}
              >
                {RALD_REALTY_CONFIG.name} Properties
              </h2>
              <div className="flex gap-2 mt-2">
                {RALD_REALTY_CONFIG.customBadges?.map((badge, index) => (
                  <span
                    key={index}
                    className="text-xs font-semibold px-3 py-1 rounded-full text-white"
                    style={{ backgroundColor: colors.secondary }}
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <p className="text-gray-600 text-lg max-w-3xl">
            Discover premium properties exclusively from{" "}
            {RALD_REALTY_CONFIG.name}, your trusted partner in real estate
            excellence.
          </p>
        </div>

        {/* Properties Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((property) => (
            <RALDRealtyCard
              key={property.id}
              property={property}
              onClick={() => onPropertyClick?.(property)}
              totalSlides={property.image_url?.length || 1}
            />
          ))}
        </div>

        {/* View All Link */}
        {properties.length > 0 && (
          <div className="mt-8 text-center">
            <button
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white transition-transform hover:scale-105 active:scale-95"
              style={{ backgroundColor: colors.primary }}
              onClick={() => {
                // Navigate to all RALD REALTY properties
                window.location.href = "/properties?partner=rald-realty";
              }}
            >
              View All {RALD_REALTY_CONFIG.name} Properties
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default RALDRealtySection;
